import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Accordion } from '@mui/material';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  LocationAlertParamAlertMethodsInner,
  SensorWiFiConfOptionsAnalysisTypeEnum,
  Threshold,
  UIOptions,
} from '../../services/api';

import RemoveDialog from '../Dialog/RemoveDialog';
import { AlertParam } from './helpers';
import AnalysisOptionsContainer, {
  AnalysisOptions,
} from './AnalysisOptions/AnalysisOptionsContainer';
import AlertMethodsFormSection from './AlertMethodsForm';
import { VarName, varNameDetails } from '../../utils/varNames';
import SensorIcon from '../../styles/icons/SensorIcon';
import UIOptionsFormSection from './UIOptionsFormSection';

export interface AlertParamAccordionProps {
  checkListId: string;
  alertParam: AlertParam;
  index: number;
  isLocation: boolean;
  onSave: (alertParam: AlertParam) => void;
  onDelete: (alertId: number) => void;
  onUpdate: (alertId: number, alertParam: AlertParam) => void;
  onChangeParam: (patch: Partial<AlertParam>) => void;
  onChangeAnalysis: (patch: Partial<AnalysisOptions>) => void;
  changeStatus: (value: AlertParam) => void;
  cancelHandler: (value: number) => void;
}

export default function AlertParamAccordion({
  checkListId,
  alertParam,
  index,
  isLocation,
  onSave,
  onDelete,
  onUpdate,
  onChangeParam,
  onChangeAnalysis,
  changeStatus,
  cancelHandler,
}: AlertParamAccordionProps) {
  const theme = useTheme();
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { alertStatus, alertId, analysisOptions } = alertParam;
  const { analysisType } = analysisOptions;
  const isWifiConf = analysisType === SensorWiFiConfOptionsAnalysisTypeEnum.SensorWiFiConf;
  const varName = !isWifiConf ? analysisOptions.varName : VarName.ClientsWiFi;
  const threshold = !isWifiConf ? (analysisOptions.threshold as Threshold) : undefined;
  const { icon, label, metric } = varName
    ? varNameDetails[varName]
    : { icon: SensorIcon, label: '', metric: '' };
  const Icon = icon ?? SensorIcon;

  const getLabelSummary = () => {
    const parts: string[] = [];

    if (threshold?.minValue !== undefined)
      parts.push(`below ${threshold.minValue} ${metric ?? ''}`);
    if (threshold?.maxValue !== undefined)
      parts.push(`above ${threshold.maxValue} ${metric ?? ''}`);
    if (threshold?.minRange !== undefined) parts.push(`min range ${threshold.minRange}`);
    if (threshold?.maxRange !== undefined) parts.push(`max range ${threshold.maxRange}`);

    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];

    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  };

  const hasExistingAlert = !!alertStatus;
  const hasActiveAlert = hasExistingAlert && alertStatus.isActive === true;
  const allowEditing = !hasExistingAlert || (hasExistingAlert && isEditing);

  return (
    <Accordion
      key={alertId ?? index}
      variant="outlined"
      defaultExpanded={!hasExistingAlert} // by default open accordion to new alert param form
      sx={{
        my: 2,
        border: `solid 1px ${theme.palette.primary.contrastText}`,
        background: theme.palette.primary.main,
      }}
      onChange={() => {
        setIsEditing(false);
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon color={`${hasActiveAlert ? 'error' : 'success'}`} />
          {hasExistingAlert ? '' : 'Creating'} Alert rules for{' '}
          {`${isWifiConf ? 'Wifi Configuration' : `${label} for`}`} {getLabelSummary()}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <RemoveDialog
          showDialog={showRemoveDialog}
          title={`Alert Param on ${label} for ${getLabelSummary()}`}
          // ✅ alertId is always current — no stale closure possible
          removeHandler={() => {
            onDelete(alertId);
            setShowRemoveDialog(false);
          }}
          cancelHandler={() => setShowRemoveDialog(false)}
        />

        <AnalysisOptionsContainer
          checkListId={checkListId}
          isLocation={isLocation}
          value={alertParam.analysisOptions}
          onChange={onChangeAnalysis}
          allowEditing={allowEditing}
        />

        <AlertMethodsFormSection
          value={alertParam.alertMethods}
          onChange={(methods: LocationAlertParamAlertMethodsInner[]) =>
            onChangeParam({ alertMethods: methods })
          }
          allowEditing={allowEditing}
        />

        <UIOptionsFormSection
          uiOptions={alertParam.uiOptions}
          onChange={(uiOptions: UIOptions) => onChangeParam({ uiOptions })}
          allowEditing={allowEditing}
        />

        <Box sx={{ mt: 2, ml: 2, display: 'flex', gap: 1 }}>
          {/* button group that allows to save or cancel creating new alerts */}
          {!hasExistingAlert && (
            <>
              <Button
                onClick={() => onSave(alertParam)}
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              >
                Save
              </Button>
              <Button
                onClick={() => cancelHandler(alertId)}
                variant="contained"
                color="error"
                startIcon={<CloseIcon />}
                size="small"
                sx={{ textTransform: 'capitalize', ml: 1 }}
              >
                Cancel
              </Button>
            </>
          )}

          {/* button group that allows editing, deleting and changing status to existing alerts */}
          {hasExistingAlert && (
            <Box>
              <Button
                onClick={() => {
                  if (isEditing) {
                    setIsEditing(false);
                    onUpdate(alertId, alertParam);
                  } else setIsEditing(true);
                }}
                variant="contained"
                color="success"
                startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              >
                {isEditing ? 'Save' : 'Edit'}
              </Button>

              {isEditing && (
                <Button
                  onClick={() => setIsEditing(false)}
                  variant="contained"
                  color="error"
                  startIcon={<CloseIcon />}
                  size="small"
                  sx={{ textTransform: 'capitalize', ml: 1 }}
                >
                  Cancel
                </Button>
              )}

              {!isEditing && (
                <>
                  <Button
                    onClick={() => changeStatus(alertParam)}
                    variant="contained"
                    color="warning"
                    startIcon={<DeleteIcon />}
                    size="small"
                    sx={{ textTransform: 'capitalize', ml: 1 }}
                  >
                    {hasActiveAlert ? 'Enable Alerts' : 'Snooze'}
                  </Button>
                  <Button
                    onClick={() => setShowRemoveDialog(true)}
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    size="small"
                    sx={{ textTransform: 'capitalize', ml: 1 }}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
